/* eslint-disable sonarjs/no-duplicate-string */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import { ModalService, ModalSettings } from 'mbs-ui-kit/modal/modal.service';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { AdministratorInCamelCase, LicenseType, PermissionsEnum } from 'mbs-ui-shared@models';
import {
  AdministratorLicense,
  ComputerGrantLicenseResultDTO,
  FilterLicenseAndAdminId,
  LicenseAssignToAdmin
} from 'mbs-ui-shared@models/License';
import { AdministratorLicenseAvailabilityInfo } from 'mbs-ui-shared@models/administrators/administrator-license-availability-info';
import { AdministratorLicenseSummaryInfo } from 'mbs-ui-shared@models/licenses/administrator-license-summary-info';
import { LicenseSummary } from 'mbs-ui-shared@models/licenses/license-summary';
import { AssignLicenseDialogueData } from 'mbs-ui-shared@modules/rmm-licenses/components/assign-license-dialogue/assign-license-dialogue-data';
import { LicensesService } from 'mbs-ui-shared@services/licenses.service';
import { LicensesStoreActions, LicensesStoreSelectors } from 'mbs-ui-shared@store/licenses';
import { LicensesHelper } from 'mbs-ui-shared@utils/helpers/licenses-helper';
import moment from 'moment';
import { Observable, catchError, filter, finalize, from, map, of, skip, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { AdministratorsFacade } from './administrators.facade';

@Injectable({
  providedIn: 'root'
})
export class LicensesFacade {
  public balance = this.store.select(LicensesStoreSelectors.selectBalance);

  public types = this.store.select(LicensesStoreSelectors.selectLicenseTypes);
  public typesMap = this.store.select(LicensesStoreSelectors.selectLicenseTypesMap);

  public permissionFlags = this.store.select(LicensesStoreSelectors.selectPermissionFlags);
  public permissionFlagsLoaded = this.store.select(LicensesStoreSelectors.selectPermissionFlagsLoaded);

  public assignedLicense = this.store.select(LicensesStoreSelectors.selectAssignedLicense);
  public loading = this.store.select(LicensesStoreSelectors.selectLoading);

  constructor(
    protected licensesService: LicensesService,
    protected modal: ModalService,
    protected i18nPipe: I18NextPipe,
    protected auth: AuthFacade,
    protected adminFacade: AdministratorsFacade,
    protected router: Router,
    protected ability: AbilityService,
    protected store: Store
  ) {
    this.assignedLicense
      .pipe(
        withLatestFrom(this.auth.currentUser$),
        filter(([license, user]) => user && license && license.adminId === user.Id),
        switchMap(() => this.auth.refreshToken$().pipe(catchError(() => of())))
      )
      .subscribe();
  }

  public loadTypes() {
    this.store.dispatch(LicensesStoreActions.loadLicenseTypes());
  }

  public loadBalance() {
    this.store.dispatch(LicensesStoreActions.loadBalance());
  }

  public loadPermissionFlags(force = false) {
    this.store.dispatch(LicensesStoreActions.loadPermissionFlags({ force }));
  }

  public grantLicenseByType(hid: string, type: string): Observable<unknown> {
    return this.licensesService.grantLicenseByType(hid, type);
  }

  public assignLicenseToAdmin(administratorId: string, licenseType: LicenseType, isTrial: boolean): Observable<AdministratorLicense> {
    return this.handleLoading(
      this.licensesService
        .assignLicense(administratorId, licenseType, isTrial)
        .pipe(switchMap(() => this.updateAdministratorLicense(administratorId)))
    );
  }

  public getAvailableLicenseSummaries(): Observable<AdministratorLicenseSummaryInfo[]> {
    return this.handleLoading(this.licensesService.getAvailableLicenseSummaries());
  }

  protected getFilteredLicenseItems(filter: LicenseType[]): Observable<Array<AdministratorLicenseSummaryInfo>> {
    return this.getAvailableLicenseSummaries().pipe(
      switchMap((licenses) => of(filter?.length ? licenses.filter((x) => filter.includes(x.licenseType)) : licenses))
    );
  }

  protected assignLicenseOrOpenDialog$(data: FilterLicenseAndAdminId, isRmm: boolean): Observable<AdministratorLicense | boolean> {
    return this.hasAvailableLicense(isRmm, data).pipe(
      switchMap((hasAvailableLicense) => {
        return hasAvailableLicense && isRmm
          ? this.assignLicenseToAdmin(data.adminID, isRmm ? LicenseType.RMM : LicenseType.RemoteDesktop, false)
          : this.openAdminLicenseModal(data);
      })
    );
  }

  protected hasAvailableLicense(isRmm: boolean, data: FilterLicenseAndAdminId) {
    return this.getFilteredLicenseItems(data.filter).pipe(
      switchMap((licenses) => {
        const rmmLicense = licenses?.find((l) => l.licenseType === LicenseType.RMM);
        const rdLicense = !isRmm ? licenses?.find((l) => l.licenseType === LicenseType.RemoteDesktop) : null;
        return of(isRmm ? rmmLicense?.count : (!rdLicense && rmmLicense?.count) || (!rmmLicense && rdLicense?.count));
      })
    );
  }

  protected assignLicense(data: FilterLicenseAndAdminId, isRmm: boolean): Observable<null> {
    const trialRmm = this.isTrialLicenseAvailable(LicenseType.RMM);
    const trialRD = this.isTrialLicenseAvailable(LicenseType.RemoteDesktop);
    const licenseType = isRmm ? LicenseType.RMM : trialRD ? LicenseType.RemoteDesktop : LicenseType.RMM;

    return (isRmm ? trialRmm : trialRmm || trialRD)
      ? this.assignLicenseToAdmin(data.adminID, licenseType, true).pipe(map(() => null))
      : this.assignLicenseOrOpenDialog$(data, isRmm).pipe(map(() => null));
  }

  public grantLicense(licenseType: LicenseType, entityId: string, trial: boolean): Observable<AdministratorLicense> {
    return this.handleLoading(
      this.assignLicenseToAdmin(entityId, licenseType, trial).pipe(
        switchMap((license) => this.auth.fetchCurrentUser$.pipe(map(() => license)))
      )
    );
  }

  public removeLicense(administratorId: string): Observable<AdministratorLicense> {
    return this.handleLoading(
      this.licensesService.removeLicense(administratorId).pipe(switchMap(() => this.updateAdministratorLicense(administratorId)))
    );
  }

  public disableAutoRenewal(administratorId: string): Observable<AdministratorLicense> {
    return this.handleLoading(
      this.licensesService.disableAutoRenewal(administratorId).pipe(switchMap(() => this.updateAdministratorLicense(administratorId)))
    );
  }

  public updateAdministratorLicense(administratorId: string): Observable<AdministratorLicense> {
    return of(administratorId).pipe(
      switchMap((adminId) => this.adminFacade.getById$(adminId, true, true)),
      skip(1),
      filter(Boolean),
      take(1),
      map((admin) => admin.license),
      tap((license: AdministratorLicense) =>
        this.store.dispatch(
          LicensesStoreActions.setAssignedLicense({
            license: { adminId: administratorId, license }
          })
        )
      ),
      switchMap((license) => this.auth.fetchCurrentUser$.pipe(map(() => license)))
    );
  }

  public isTrialLicenseAvailable(licenseType: LicenseType = null): boolean {
    const trialLicenses = this.auth?.currentUserValue?.AdminTrialLicences;

    if (!trialLicenses) {
      return false;
    }

    const isValidLicense = (license: AdministratorLicenseAvailabilityInfo) => {
      return license && license.Allowed && (!license.ExpiredDate || moment(license.ExpiredDate).isAfter(new Date()));
    };

    if (!isNil(licenseType)) {
      return isValidLicense(trialLicenses[licenseType]);
    } else {
      return Object.values(trialLicenses).some((license) => isValidLicense(license));
    }
  }

  public isLicenseExpired(license: AdministratorLicense): boolean {
    return LicensesHelper.IsLicenseExpired(license);
  }

  public isExpiresSoon(license: AdministratorLicense): boolean {
    const offset = 5;
    const now = new Date();
    now.setDate(now.getDate() + offset);

    return !license.isAutoRenew && moment(now).isSameOrAfter(license.dateExpired);
  }

  public buyLicenseByType(type: LicenseType): void {
    const rules = [
      { type: LicenseType.RMM, param: ['rmm'] },
      { type: LicenseType.RemoteDesktop, param: ['rmm', 'rd'] }
    ];

    this.buyLicense(rules.find((rule) => rule.type === type)?.param || []);
  }

  public buyLicense(licenses: string | string[] = 'rd,rmm'): void {
    const param = Array.isArray(licenses) ? licenses.join(',') : licenses;
    const link = this.ability.can('read', PermissionsEnum.AllowPrePaymentLicensePage) ? RoutingPath.ApCalc : RoutingPath.ApLicensesAspx;

    location.href = `${link}?buy=true&licenses=${param}`;
  }

  public refreshAuth(): void {
    this.auth.fetchCurrentUser$.pipe(take(1)).subscribe();
  }

  protected prepareOpenGrantLicenseDialogueModalSettings(
    adminId: string,
    filter: LicenseType[] = null,
    withTrials = false,
    defaultFilter = true,
    email = ''
  ): ModalSettings {
    const settings = new ModalSettings();
    settings.responsive = true;
    settings.data = {
      title: this.i18nPipe.transform('shared:licenses.manageModal:assignTitle', {
        format: 'title'
      }),
      entityId: adminId,
      adminEmail: email,
      startTrial: false,
      showTrials: withTrials,
      filterLicensesFunc$: defaultFilter ? this.filterLicensesFunc.bind(this) : this.filterLicensesFuncForRM.bind(this),
      showBuyBtn: this.ability.can('read', PermissionsEnum.LicenseBuy)
    } as AssignLicenseDialogueData;
    if (filter && filter.length) settings.data.licenseTypesFilter = filter;

    return settings;
  }

  public assignLicenseDialogueModal(data: FilterLicenseAndAdminId): Observable<LicenseAssignToAdmin> {
    const isRmm = data.filter && data.filter.length === 1 && data.filter[0] === LicenseType.RMM;

    return this.getFilteredLicenseItems(data.filter).pipe(
      take(1),
      switchMap(() => this.handleAssignLicense(isRmm, data)),
      switchMap(() => this.assignedLicense)
    );
  }

  public tryGrantLicense(hid: string): Observable<ComputerGrantLicenseResultDTO> {
    return this.licensesService.tryGrantLicense(hid);
  }

  protected handleAssignLicense(isRmm, data: FilterLicenseAndAdminId) {
    return data.expiredCurrent ? this.assignLicenseOrOpenDialog$(data, isRmm) : this.assignLicense(data, isRmm);
  }

  protected prepareAssignLicenseModalText(isRmm: boolean, action: string): string {
    const product = this.i18nPipe.transform(isRmm ? 'shared:products.rmm' : 'shared:products.remote_desktop');

    const text = this.ability.can('read', PermissionsEnum.Licenses)
      ? this.i18nPipe.transform('shared:licenses.assignLicenseNow', { action })
      : '<br>Please contact your service provider.';

    return `A license is required to use <b>${product}</b>. ${text}`;
  }

  protected filterLicensesFuncForRM(adminId: string, licenses: LicenseSummary[]): Observable<LicenseSummary[]> {
    return of(true).pipe(
      switchMap(() => {
        licenses.forEach((license) => (license.canTrial = this.isTrialLicenseAvailable(license.licenseType)));

        return of(licenses);
      })
    );
  }

  protected filterLicensesFunc(adminId: string, licenses: LicenseSummary[]): Observable<LicenseSummary[]> {
    return this.adminFacade.getById$(adminId, true).pipe(
      filter(Boolean),
      switchMap((admin: AdministratorInCamelCase) => {
        const isRMM = admin?.license?.licenseType === LicenseType.RMM;

        licenses.forEach((license) => {
          license.canTrial = this.isTrialLicenseAvailable(license.licenseType);
          license.show = isRMM && license.licenseType === LicenseType.RMM;
        });

        return of(licenses);
      })
    );
  }

  public openGrantLicenseDialogueWithInfoModal(data: FilterLicenseAndAdminId, action?: string): Observable<LicenseAssignToAdmin> {
    return this.openAssignLicenseModal(data, action).pipe(
      switchMap(({ result, isRmm }) => (result ? this.handleAssignLicense(isRmm, data) : of(null))),
      catchError(() => {
        window?.sessionStorage?.removeItem('btnClientId');
        return of(null);
      }),
      switchMap(() => this.assignedLicense)
    );
  }

  public openGrantLicenseDialogue(
    adminId: string,
    filter: LicenseType[] = null,
    withTrials = false,
    defaultFilter = true,
    email = '',
    modal?: any
  ): Observable<boolean> {
    const settings = this.prepareOpenGrantLicenseDialogueModalSettings(adminId, filter, withTrials, defaultFilter, email);

    return from(this.modal.openCustom(modal, settings)).pipe(
      map(() => true),
      catchError(() => of(null))
    );
  }

  public openAdminLicenseModal(data: FilterLicenseAndAdminId): Observable<boolean> {
    console.log('Method not implemented');
    throw new Error('Method not implemented');
  }

  public prepareAssignLicenseModalSettings(action: string): ModalSettings {
    const modalSettings: ModalSettings = {
      header: { title: this.i18nPipe.transform('shared:licenses.common.licenseRequired') },
      footer: {
        okButton: { show: false },
        cancelButton: { text: this.i18nPipe.transform('shared:buttons.cancel'), type: 'secondary' }
      }
    };

    if (this.ability.can('read', PermissionsEnum.Licenses)) {
      modalSettings.footer.okButton = {
        text: this.i18nPipe.transform(action),
        type: 'success'
      };
    }

    return modalSettings;
  }

  public openAssignLicenseModal(data: FilterLicenseAndAdminId, action: string) {
    const isRmm = data.filter && data.filter.length === 1 && data.filter[0] === LicenseType.RMM;

    const modalSettings = this.prepareAssignLicenseModalSettings(action);
    const modalText = this.prepareAssignLicenseModalText(isRmm, action);

    return from(this.modal.open(modalSettings, modalText)).pipe(map((result) => ({ result, isRmm })));
  }

  public getSubscriptionEditLink(): Observable<string> {
    return this.licensesService.getSubscriptionEditLink();
  }

  private handleLoading<T>(observable: Observable<T>): Observable<T> {
    this.store.dispatch(LicensesStoreActions.setLoading({ loading: true }));
    return observable.pipe(finalize(() => this.store.dispatch(LicensesStoreActions.setLoading({ loading: false }))));
  }
}
